import React, { useState } from 'react'
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import { useCanary } from '@bill/canary.react'
import Form from '@divvy-web/skylab.form'
import Spinner from '@divvy-web/skylab.spinner'

import ErrorPage from '@sputnik/components/ErrorPage'
import useApplicantTokenOnMount from '@sputnik/hooks/useApplicantTokenOnMount'
import { initialValues } from '@sputnik/pages/FormPages/initialValues'
import { regularPages } from '@sputnik/pages/FormPages/pages'
import GetCreditApplication from '@sputnik/pages/gql/GetCreditApplication.gql'
import NotFound from '@sputnik/pages/NotFound'
import { CREDIT_APPLICATION, MAJOR_ACCOUNT_CHANGE, MIGRATION, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { isRequestingCorrection } from '@sputnik/utils/dataUtils'
import PhoneNumberFieldInputProvider from '@sputnik/utils/PhoneNumberFieldInputProvider'
import { RawCreditAppContext } from '@sputnik/utils/RawCreditAppContext'

import FormPageWrapper from './FormPageWrapper'
import getValidations from './getValidations'

const FormWrapper = () => {
  const params = useParams()
  const showWebBank = useCanary('show-web-bank') as boolean
  const [searchParams] = useSearchParams()
  const appId = params.appId
  const [isSaving, setIsSaving] = useState(false)
  const [shouldShowIncompleteBanner, setShouldShowIncompleteBanner] = useState(false)
  const [clickedSaveAndExit, setClickedSaveAndExit] = useState(false)

  useApplicantTokenOnMount({ salesforceCreditId: appId || '' })

  const { data, error, loading, refetch } = useQuery(GetCreditApplication, {
    fetchPolicy: 'network-only',
    variables: { creditApplicationId: appId },
  })

  if (loading) return <Spinner fullPage />
  if (error) return <ErrorPage />

  const creditApp = data?.creditApplication
  const { appVersion, salesforceCreditId, bdcOrgId, recordType } = creditApp ?? {}

  if (appVersion >= 3) {
    return (
      <Navigate
        replace
        to={`/app/${salesforceCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`}
      />
    )
  }

  const isMigration = recordType === MIGRATION
  const isMajorAccountChange = recordType === MAJOR_ACCOUNT_CHANGE
  const requestingCorrection = creditApp && isRequestingCorrection(creditApp)
  const formInitialValues = initialValues(creditApp, CREDIT_APPLICATION)
  const pages = regularPages(isMigration, requestingCorrection, isMajorAccountChange, showWebBank)
  const formPages = pages.map((page) => {
    const { component, description, hidePrevious, index, mutationKey, name, route, title } = page

    return (
      <Route
        key={'app-form-page-' + index}
        element={
          <FormPageWrapper
            bdcOrgId={bdcOrgId}
            clickedSaveAndExit={clickedSaveAndExit}
            hidePrevious={hidePrevious}
            isMigration={isMigration}
            mutationKey={mutationKey}
            pageCount={pages.length}
            pageIndex={index}
            pageName={name}
            pages={pages}
            pageSubTitle={description}
            pageTitle={title}
            refetchFormValues={refetch}
            setClickedSaveAndExit={setClickedSaveAndExit}
            setIsSaving={setIsSaving}
            setShouldShowIncompleteBanner={setShouldShowIncompleteBanner}
            shouldShowIncompleteBanner={shouldShowIncompleteBanner}
          >
            {component({
              alwaysShowError: shouldShowIncompleteBanner && !clickedSaveAndExit,
              isMigration: isMigration,
              isSaving: isSaving,
              serverBusinessValues: { admin: creditApp?.admin, ...creditApp?.businessInfo },
            })}
          </FormPageWrapper>
        }
        path={route}
      />
    )
  })

  return (
    <section>
      <div className='form-container'>
        <Form
          initialValues={formInitialValues}
          validation={(formValues) =>
            getValidations({
              formValues,
              owner: searchParams.get('owner'),
              pageName: params['*'],
              requestedCorrections: requestingCorrection && creditApp?.sectionsToCorrect,
            })
          }
        >
          <PhoneNumberFieldInputProvider>
            <RawCreditAppContext value={{ ...creditApp }}>
              <Routes>
                {formPages}
                <Route
                  element={<NotFound />}
                  path='/*'
                />
              </Routes>
            </RawCreditAppContext>
          </PhoneNumberFieldInputProvider>
        </Form>
      </div>
    </section>
  )
}

export default FormWrapper
